import type { EChartsOption } from "echarts";
import type {
  ChartType,
  GeneralizedChartDefinition,
  MeasureEncoding,
} from "@/features/chat/model/chatTypes";

export const ALL_CHART_TYPES: ChartType[] = ["line", "bar", "area", "pie", "scatter", "combo"];

/** Text colors ECharts needs as literal values — it paints via canvas, which cannot resolve CSS custom properties like `var(--theme-text-secondary)`. Callers must source these from a theme config (see `src/config/themes/`), never hardcode them. */
export type ChartTextColors = {
  secondary: string;
  disabled: string;
};

/**
 * Charts with many measures/stack values produce more legend entries than fit
 * on one row. A plain legend wraps onto extra rows that overflow upward into
 * the plot area (the grid's reserved bottom offset only accounts for one
 * row), so legends use ECharts' scroll mode instead: it keeps the legend to a
 * single row and paginates with prev/next arrows once entries overflow the
 * available width, without needing to predict the entry count up front.
 */
function buildScrollableLegendBase(textColors: ChartTextColors, selected?: Record<string, boolean>) {
  return {
    type: "scroll" as const,
    bottom: 0,
    textStyle: { color: textColors.secondary },
    pageIconColor: textColors.secondary,
    pageIconInactiveColor: textColors.disabled,
    pageTextStyle: { color: textColors.secondary },
    ...(selected ? { selected } : {}),
  };
}

/**
 * Height in px of one measure's legend row in a multi-measure chart with a
 * stack_dimension (or, for scatter, a multi-measure chart in general),
 * including the gap to the row below it. Chosen to match the single-row
 * layout's existing item height (14px) plus its label/padding.
 */
const STACKED_LEGEND_ROW_HEIGHT_PX = 24;

/**
 * Splits legend entries into one scrollable row per measure so a reader
 * isn't forced to page through unrelated measures to find one stack value's
 * entry (e.g. one row for "China (Net Revenue)" / "US (Net Revenue)", a
 * second for "China (WoW Delta)" / "US (WoW Delta)"). A single measure
 * doesn't have this fan-out problem, so it keeps one plain row.
 */
function buildPerMeasureLegendRows(
  measureCount: number,
  textColors: ChartTextColors,
  legendNamesByMeasure: string[][],
  selected?: Record<string, boolean>,
): EChartsOption["legend"] {
  return measureCount <= 1
    ? { ...buildScrollableLegendBase(textColors, selected), data: legendNamesByMeasure[0] ?? [] }
    : legendNamesByMeasure.map((names, measureIndex) => ({
        ...buildScrollableLegendBase(textColors, selected),
        data: names,
        // measureIndex 0 renders as the topmost row (closest to the plot).
        bottom: (measureCount - 1 - measureIndex) * STACKED_LEGEND_ROW_HEIGHT_PX,
      }));
}

/** Font size in px shared by every axis name and legend caption — one step
 * below the tick labels so a name reads as a caption rather than as data. */
const AXIS_NAME_FONT_SIZE_PX = 11;

/**
 * Vertical distance in px from the x-axis line down to the top of the
 * dimension name (see buildDimensionAxisName). Sized to clear the tick labels
 * under the axis — ECharts' default 8px `axisLabel.margin` plus a ~12px label
 * line — so the name lands in the gap between those labels and the legend.
 */
const X_AXIS_NAME_TOP_OFFSET_PX = 30;

/** Distance in px from the panel bottom to a caption naming the field whose
 * values the legend lists: one legend row's worth of space above the bottom. */
const LEGEND_CAPTION_BOTTOM_PX = 22;

/** Bottom strip in px a pie leaves free for the legend row plus the caption
 * above it, so neither the slices nor their outer labels overlap either. */
const PIE_LEGEND_AREA_HEIGHT_PX = 40;

/**
 * Places the dimension name below the x-axis tick labels, right-aligned to the
 * end of the axis.
 *
 * ECharts anchors an x-axis name past the *right end* of the axis line
 * (`nameLocation: "end"`), where the panel's narrow right margin clips all but
 * its first letter — "Month" renders as "M". Anchoring the text's right edge
 * to that same point instead keeps the whole name within the plot's width, and
 * offsetting it downwards drops it clear of the tick labels into the gap above
 * the legend. The offset goes through `nameTextStyle.padding` because
 * `nameGap` moves an end-anchored name *along* the axis, not away from it.
 * `nameMoveOverlap` must stay off: ECharts would otherwise resolve the overlap
 * it sees with the tick labels by pushing the name back out along the axis,
 * off-canvas again.
 */
function buildDimensionAxisName(name: string | null | undefined, textColors: ChartTextColors) {
  return {
    name: name ?? undefined,
    nameLocation: "end" as const,
    nameGap: 0,
    nameMoveOverlap: false,
    nameTextStyle: {
      color: textColors.secondary,
      fontSize: AXIS_NAME_FONT_SIZE_PX,
      align: "right" as const,
      verticalAlign: "top" as const,
      padding: [X_AXIS_NAME_TOP_OFFSET_PX, 0, 0, 0],
    },
  };
}

/**
 * Styling for a measure's y-axis name. ECharts already places it above the
 * axis, where it needs no repositioning — it only needs the theme's text
 * color, since ECharts otherwise falls back to its own light-theme default.
 */
function buildMeasureAxisNameTextStyle(textColors: ChartTextColors) {
  return { color: textColors.secondary, fontSize: AXIS_NAME_FONT_SIZE_PX };
}

/**
 * Caption naming the field whose values the legend lists (e.g. "Country" over
 * a legend of country names). ECharts has no legend title, so it renders as a
 * bottom-anchored `title` sitting just above the legend row(s) it describes.
 */
function buildLegendCaption(
  label: string | null | undefined,
  textColors: ChartTextColors,
  bottom: number,
): EChartsOption["title"] {
  return label
    ? {
        text: label,
        left: "center",
        bottom,
        textStyle: {
          fontSize: AXIS_NAME_FONT_SIZE_PX,
          fontWeight: 400,
          color: textColors.secondary,
        },
      }
    : undefined;
}

function toNumeric(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getAvailableChartTypes(chart: GeneralizedChartDefinition): ChartType[] {
  const measureCount = chart.measures.length;
  const dimensionType = chart.dimension.type ?? "category";

  if (measureCount === 0) {
    return [];
  }

  if (measureCount === 1) {
    if (dimensionType === "number") {
      return ["scatter", "line", "bar"];
    }
    if (dimensionType === "time") {
      return ["line", "bar", "area"];
    }
    return ["line", "bar", "area", "pie"];
  }

  return ["line", "bar", "area", "combo", "scatter"];
}

function resolveSeriesType(
  selectedChartType: ChartType,
  measureEncoding: MeasureEncoding | null | undefined,
): "line" | "bar" {
  if (selectedChartType === "combo") {
    return measureEncoding === "bar" ? "bar" : "line";
  }
  if (selectedChartType === "bar") {
    return "bar";
  }
  return "line";
}

/** How many times larger one measure's scale must be than the other's before
 * they're split across two axes — a second axis only earns its keep when
 * sharing one would otherwise flatten the smaller measure into a hairline. */
const DUAL_AXIS_SCALE_RATIO = 5;

function measureAbsoluteScale(data: Record<string, unknown>[], measureKey: string): number {
  let max = 0;
  for (const row of data) {
    const raw = row[measureKey];
    if (raw == null) {
      continue;
    }
    const value = Math.abs(toNumeric(raw));
    if (value > max) {
      max = value;
    }
  }
  return max;
}

/**
 * Splits measures across two y-axes only when there are exactly two: the
 * first measure keeps the primary (left) axis and the second moves to the
 * secondary (right) axis, so each gets its own scale (e.g. "Agent Count" vs.
 * "Total Lifetime Premium"). With one measure, or with three or more, there's
 * no unambiguous way to divide them across two axes, so every measure shares
 * the primary axis instead. Applies uniformly to every chart type — line,
 * area, bar, combo, and scatter — rather than varying by how the measure is
 * rendered.
 *
 * Within the two-measure case, splitting only helps when the measures' scales
 * are actually far apart — two measures with comparable ranges (e.g. revenue
 * vs. cost) read just fine sharing one axis, and giving them separate scales
 * would only make them harder to compare. So the axes are split only when one
 * measure's largest absolute value is at least DUAL_AXIS_SCALE_RATIO times
 * the other's, unless the chart designer already pinned an explicit
 * y_axis_index, which is always respected as-is.
 */
function resolveYAxisIndexByMeasure(
  measures: GeneralizedChartDefinition["measures"],
  data: Record<string, unknown>[],
): number[] {
  if (measures.length !== 2) {
    return measures.map(() => 0);
  }
  if (measures.some((measure) => measure.y_axis_index === 0 || measure.y_axis_index === 1)) {
    return measures.map((measure, index) =>
      measure.y_axis_index === 0 || measure.y_axis_index === 1 ? measure.y_axis_index : index,
    );
  }
  const scales = measures.map((measure) => measureAbsoluteScale(data, measure.key));
  const maxScale = Math.max(...scales);
  const minScale = Math.min(...scales);
  const hasHugeDifference = maxScale > 0 && (minScale === 0 || maxScale / minScale >= DUAL_AXIS_SCALE_RATIO);
  return hasHugeDifference ? measures.map((_, index) => index) : measures.map(() => 0);
}

function measureSignedRange(
  data: Record<string, unknown>[],
  measureKeys: string[],
): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const row of data) {
    for (const key of measureKeys) {
      const raw = row[key];
      if (raw == null) {
        continue;
      }
      const value = toNumeric(raw);
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

/** Fraction of the axis height, measured up from the bottom, where 0 falls
 * for this range once it's widened to include 0. */
function zeroHeightRatio(range: { min: number; max: number } | null): number {
  if (!range) {
    return 0;
  }
  const min = Math.min(range.min, 0);
  const max = Math.max(range.max, 0);
  const span = max - min;
  return span > 0 ? (0 - min) / span : 0;
}

/**
 * Two value axes on the same grid always share the same pixel row for their
 * own min and their own max — but NOT for 0, unless 0 sits at the same
 * fraction of each axis's own range. A purely positive axis (min 0) has its
 * zero pinned to the very bottom of the plot, while a second axis spanning
 * both signs has its zero somewhere in the middle. Left alone, a negative
 * value on the second axis can sit entirely above the first axis's "0"
 * gridline — numerically negative, but never visibly crossing the only zero
 * line the reader sees. Widening each axis's range (purely for display; it
 * never trims real data) so 0 lands at the same height fraction on both
 * fixes that: whichever axis naturally needs more headroom below zero sets
 * the shared ratio, and the other axis is stretched to match.
 */
function alignedAxisRange(
  range: { min: number; max: number } | null,
  zeroRatio: number,
): { min: number; max: number } | undefined {
  if (!range) {
    return undefined;
  }
  const min = Math.min(range.min, 0);
  const max = Math.max(range.max, 0);
  if (zeroRatio <= 0 || zeroRatio >= 1) {
    return { min, max };
  }
  const negativeSpan = 0 - min;
  const positiveSpan = max;
  const totalForNegative = negativeSpan > 0 ? negativeSpan / zeroRatio : 0;
  const totalForPositive = positiveSpan > 0 ? positiveSpan / (1 - zeroRatio) : 0;
  const total = Math.max(totalForNegative, totalForPositive, negativeSpan + positiveSpan);
  return { min: -(total * zeroRatio), max: total * (1 - zeroRatio) };
}

type DualAxisMeta = {
  keys: [string[], string[]];
  names: [string | undefined, string | undefined];
  hidden: [boolean, boolean];
};

/**
 * Resolves everything the dual-axis yAxis needs for the exactly-two-measure
 * split: which measure keys feed each axis's range, the label to print above
 * each axis (so a reader can tell "Agent Count" from "Total Lifetime
 * Premium" apart without relying on series color alone), and whether that
 * axis's one measure has been toggled off via the legend — in which case the
 * axis itself hides along with its series instead of showing an unlabeled,
 * unused scale.
 */
function buildDualAxisMeta(
  measures: GeneralizedChartDefinition["measures"],
  yAxisIndexByMeasure: number[],
  hiddenMeasureKeys: Set<string>,
): DualAxisMeta {
  const forAxis = (axisIndex: number) => measures.filter((_, index) => yAxisIndexByMeasure[index] === axisIndex);
  const axis0 = forAxis(0);
  const axis1 = forAxis(1);
  return {
    keys: [axis0.map((measure) => measure.key), axis1.map((measure) => measure.key)],
    names: [
      axis0[0] ? axis0[0].label ?? axis0[0].key : undefined,
      axis1[0] ? axis1[0].label ?? axis1[0].key : undefined,
    ],
    hidden: [
      Boolean(axis0[0] && hiddenMeasureKeys.has(axis0[0].key)),
      Boolean(axis1[0] && hiddenMeasureKeys.has(axis1[0].key)),
    ],
  };
}

/**
 * Builds the dual-axis yAxis pair with 0 aligned to the same height on both
 * sides (see alignedAxisRange). Falls back to plain auto-ranged axes when
 * either side has no data to range over. Each axis is labeled with its one
 * measure's name, and hides entirely (line, ticks, labels, name) once that
 * measure has been deselected via the legend.
 */
function buildAlignedDualYAxis(
  textColors: ChartTextColors,
  data: Record<string, unknown>[],
  meta: DualAxisMeta,
): EChartsOption["yAxis"] {
  const ranges = meta.keys.map((keys) => measureSignedRange(data, keys));
  const zeroRatio = Math.max(zeroHeightRatio(ranges[0]), zeroHeightRatio(ranges[1]));
  const aligned = ranges.map((range) => alignedAxisRange(range, zeroRatio));
  return [
    {
      type: "value",
      show: !meta.hidden[0],
      name: meta.hidden[0] ? undefined : meta.names[0],
      nameGap: 18,
      nameTextStyle: buildMeasureAxisNameTextStyle(textColors),
      axisLabel: { color: textColors.secondary },
      splitLine: { show: !meta.hidden[0] },
      min: aligned[0]?.min,
      max: aligned[0]?.max,
    },
    {
      type: "value",
      show: !meta.hidden[1],
      name: meta.hidden[1] ? undefined : meta.names[1],
      nameGap: 18,
      nameTextStyle: buildMeasureAxisNameTextStyle(textColors),
      axisLabel: { color: textColors.secondary },
      splitLine: { show: false },
      min: aligned[1]?.min,
      max: aligned[1]?.max,
    },
  ];
}

export function buildChartOption(
  chart: GeneralizedChartDefinition,
  data: Record<string, unknown>[],
  selectedChartType: ChartType,
  palette: string[],
  textColors: ChartTextColors,
  hiddenMeasureKeys: Set<string> = new Set(),
): EChartsOption {
  const xKey = chart.dimension.key;
  const categories = data.map((row) => String(row[xKey] ?? ""));

  if (selectedChartType === "pie") {
    const measureKey = chart.measures[0]?.key;
    if (!measureKey) {
      return {};
    }
    const stackKey = chart.stack_dimension?.key;
    const sliceData = stackKey
      ? Array.from(
          data.reduce((totals, row) => {
            const name = String(row[stackKey] ?? "");
            const value = row[measureKey] == null ? 0 : toNumeric(row[measureKey]);
            totals.set(name, (totals.get(name) ?? 0) + value);
            return totals;
          }, new Map<string, number>()),
        ).map(([name, value]) => ({ name, value }))
      : data.map((row) => ({
          name: String(row[xKey] ?? ""),
          value: Number(row[measureKey] ?? 0),
        }));

    const sliceFieldLabel = stackKey ? chart.stack_dimension?.label : chart.dimension.label;
    return {
      color: palette,
      tooltip: { trigger: "item" },
      title: buildLegendCaption(sliceFieldLabel, textColors, LEGEND_CAPTION_BOTTOM_PX),
      legend: { ...buildScrollableLegendBase(textColors) },
      series: [
        {
          type: "pie",
          radius: "60%",
          bottom: PIE_LEGEND_AREA_HEIGHT_PX,
          label: { color: textColors.secondary },
          data: sliceData,
        },
      ],
    };
  }

  if (selectedChartType === "scatter") {
    const measureCount = chart.measures.length;
    if (measureCount === 0) {
      return {};
    }
    const dimensionType = chart.dimension.type ?? "category";
    // A time-typed dimension must keep its raw value (e.g. an ISO date
    // string) for ECharts' time axis to parse it, and a categorical
    // dimension (e.g. an ml_label of "HIGH"/"MEDIUM"/"LOW") must stay a
    // string for ECharts' category axis to place it on its own tick.
    // Running either through toNumeric instead coerces every non-numeric
    // string to 0, so every point collapses onto the same x position.
    const resolveX = (row: Record<string, unknown>): string | number =>
      dimensionType === "number" ? toNumeric(row[xKey]) : String(row[xKey] ?? "");
    // A category axis needs its own deduped tick list — reusing `categories`
    // (one entry per row) would repeat a tick for every row sharing that
    // category, e.g. once per stack value or measure.
    const categoryXValues: string[] = [];
    const seenCategoryX = new Set<string>();
    if (dimensionType === "category") {
      data.forEach((row) => {
        const value = String(row[xKey] ?? "");
        if (!seenCategoryX.has(value)) {
          seenCategoryX.add(value);
          categoryXValues.push(value);
        }
      });
    }
    const xAxis: EChartsOption["xAxis"] = {
      type: dimensionType === "time" ? "time" : dimensionType === "number" ? "value" : "category",
      data: dimensionType === "category" ? categoryXValues : undefined,
      ...buildDimensionAxisName(chart.dimension.label ?? xKey, textColors),
      axisLabel: { color: textColors.secondary },
    };
    const scatterYAxisIndexByMeasure = resolveYAxisIndexByMeasure(chart.measures, data);
    const scatterHasDualAxis = scatterYAxisIndexByMeasure.some((axisIndex) => axisIndex === 1);
    const scatterDualAxisMeta = scatterHasDualAxis
      ? buildDualAxisMeta(chart.measures, scatterYAxisIndexByMeasure, hiddenMeasureKeys)
      : null;
    // A single y-axis name only makes sense when every point is the same
    // measure; with several measures plotted together it would mislabel most
    // of the series, so line/bar/combo omit it the same way in that case.
    const yAxis: EChartsOption["yAxis"] = scatterDualAxisMeta
      ? buildAlignedDualYAxis(textColors, data, scatterDualAxisMeta)
      : {
          type: "value",
          name: measureCount === 1 ? chart.measures[0].label ?? chart.measures[0].key : undefined,
          nameTextStyle: buildMeasureAxisNameTextStyle(textColors),
          axisLabel: { color: textColors.secondary },
        };
    // Unlike line/bar/area, scatter points at the same x can be genuinely far
    // apart on the y-axis (e.g. a country's revenue vs. its WoW delta), so an
    // axis-trigger tooltip listing every series at that x reads as noise.
    // Item-trigger keeps the hover focused on the one point under the
    // cursor; its series name still carries the "(Measure)" suffix so it's
    // clear which measure that point belongs to.
    const tooltip: EChartsOption["tooltip"] = { trigger: "item" };

    const stackKey = chart.stack_dimension?.key;
    if (!stackKey) {
      // Every measure becomes its own scatter series (like line/bar do for
      // an unstacked multi-measure chart) instead of only ever plotting
      // measures[0] and silently dropping the rest.
      const legendSelected: Record<string, boolean> = {};
      chart.measures.forEach((measure) => {
        if (hiddenMeasureKeys.has(measure.key)) {
          legendSelected[measure.label ?? measure.key] = false;
        }
      });
      return {
        color: palette,
        tooltip,
        legend: {
          ...buildScrollableLegendBase(textColors, legendSelected),
          data: chart.measures.map((m) => m.label ?? m.key),
        },
        grid: { left: 48, right: scatterHasDualAxis ? 60 : 24, top: 32, bottom: 64, containLabel: true },
        xAxis,
        yAxis,
        series: chart.measures.map((measure, index) => ({
          name: measure.label ?? measure.key,
          type: "scatter",
          yAxisIndex: scatterYAxisIndexByMeasure[index],
          data: data
            .filter((row) => row[measure.key] != null)
            .map((row) => [resolveX(row), toNumeric(row[measure.key])]),
        })),
      } as EChartsOption;
    }

    // With a stack_dimension (e.g. one series per country) and multiple
    // measures, split the points into one scatter series per stack
    // value/measure pair — matching how line/bar/area handle the same shape
    // — instead of collapsing every stack value into a single blob and
    // silently dropping every measure but the first.
    const stackValues: string[] = [];
    const seenStack = new Set<string>();
    data.forEach((row) => {
      const stackLabel = String(row[stackKey] ?? "");
      if (!seenStack.has(stackLabel)) {
        seenStack.add(stackLabel);
        stackValues.push(stackLabel);
      }
    });

    const seriesName = (stackValue: string, measure: (typeof chart.measures)[number]): string =>
      measureCount > 1 ? `${stackValue} (${measure.label ?? measure.key})` : stackValue;

    const legendNamesByMeasure: string[][] = chart.measures.map(() => []);
    const series: NonNullable<EChartsOption["series"]> = [];
    chart.measures.forEach((measure, measureIndex) => {
      stackValues.forEach((stackValue) => {
        const name = seriesName(stackValue, measure);
        legendNamesByMeasure[measureIndex].push(name);
        series.push({
          name,
          type: "scatter",
          yAxisIndex: scatterYAxisIndexByMeasure[measureIndex],
          data: data
            .filter((row) => String(row[stackKey] ?? "") === stackValue && row[measure.key] != null)
            .map((row) => [resolveX(row), toNumeric(row[measure.key])]),
        });
      });
    });

    // Reserve one extra legend row's worth of height per additional measure;
    // the base 64px already covers the single-row (measureCount <= 1) case.
    const extraRowsHeight = (measureCount - 1) * STACKED_LEGEND_ROW_HEIGHT_PX;
    const legendSelected: Record<string, boolean> = {};
    chart.measures.forEach((measure) => {
      if (!hiddenMeasureKeys.has(measure.key)) {
        return;
      }
      stackValues.forEach((stackValue) => {
        legendSelected[seriesName(stackValue, measure)] = false;
      });
    });

    return {
      color: palette,
      tooltip,
      legend: buildPerMeasureLegendRows(measureCount, textColors, legendNamesByMeasure, legendSelected),
      grid: { left: 48, right: scatterHasDualAxis ? 60 : 24, top: 32, bottom: 64 + extraRowsHeight, containLabel: true },
      xAxis,
      yAxis,
      series,
    } as EChartsOption;
  }

  const isArea = selectedChartType === "area";
  const resolvedTypeByMeasure = chart.measures.map((measure) =>
    resolveSeriesType(selectedChartType, measure.encoding),
  );
  const yAxisIndexByMeasure = resolveYAxisIndexByMeasure(chart.measures, data);
  const hasDualAxis = yAxisIndexByMeasure.some((axisIndex) => axisIndex === 1);
  const dualAxisMeta = hasDualAxis ? buildDualAxisMeta(chart.measures, yAxisIndexByMeasure, hiddenMeasureKeys) : null;

  const yAxisOption: EChartsOption["yAxis"] = dualAxisMeta
    ? buildAlignedDualYAxis(textColors, data, dualAxisMeta)
    : { type: "value", axisLabel: { color: textColors.secondary } };

  const stackKey = chart.stack_dimension?.key;
  if (stackKey) {
    return buildStackedChartOption({
      chart,
      data,
      selectedChartType,
      palette,
      textColors,
      xKey,
      stackKey,
      isArea,
      hasDualAxis,
      yAxisOption,
      yAxisIndexByMeasure,
      hiddenMeasureKeys,
    });
  }

  const legendSelected: Record<string, boolean> = {};
  chart.measures.forEach((measure) => {
    if (hiddenMeasureKeys.has(measure.key)) {
      legendSelected[measure.label ?? measure.key] = false;
    }
  });

  return {
    color: palette,
    tooltip: { trigger: "axis" },
    legend: { ...buildScrollableLegendBase(textColors, legendSelected) },
    grid: { left: 48, right: hasDualAxis ? 60 : 24, top: 32, bottom: 64, containLabel: true },
    xAxis: {
      type: chart.dimension.type === "time" ? "time" : "category",
      data: chart.dimension.type === "time" ? undefined : categories,
      ...buildDimensionAxisName(chart.dimension.label ?? xKey, textColors),
      axisLabel: { color: textColors.secondary },
    },
    yAxis: yAxisOption,
    series: chart.measures.map((measure, index) => {
      const resolvedType = resolvedTypeByMeasure[index];
      return {
        name: measure.label ?? measure.key,
        type: resolvedType,
        yAxisIndex: yAxisIndexByMeasure[index],
        smooth: chart.is_trend && resolvedType === "line",
        connectNulls: false,
        lineStyle: resolvedType === "line" && measure.predicted ? { type: "dashed" } : undefined,
        itemStyle: resolvedType === "bar" && measure.predicted ? { opacity: 0.5 } : undefined,
        areaStyle: isArea ? { opacity: 0.25 } : undefined,
        data:
          chart.dimension.type === "time"
            ? data.map((row) => [row[xKey], row[measure.key] == null ? null : toNumeric(row[measure.key])])
            : data.map((row) => row[measure.key] == null ? null : toNumeric(row[measure.key])),
      };
    }),
  } as EChartsOption;
}

/**
 * A chart with a stack_dimension holds long-format rows: multiple rows share
 * the same x value, one per stack_dimension value (e.g. one row per country
 * per week). Every unique stack value gets a consistent color across measures.
 * With one measure, "China" is unambiguous and dedupes to one legend entry
 * (via ECharts merging series with the same `name`). With multiple measures,
 * each measure becomes its own group of bars/lines (bars additionally stack
 * within a measure group via ECharts' `stack` field) and series are named
 * "China (Net Revenue)" / "China (WoW Difference)" so the legend and the
 * axis tooltip both show which value belongs to which measure. The legend
 * itself gets one scrollable row per measure (rather than a single row
 * interleaving every measure's stack values) so a reader isn't forced to
 * page through unrelated measures to find one country's entry.
 */
function buildStackedChartOption(options: {
  chart: GeneralizedChartDefinition;
  data: Record<string, unknown>[];
  selectedChartType: ChartType;
  palette: string[];
  textColors: ChartTextColors;
  xKey: string;
  stackKey: string;
  isArea: boolean;
  hasDualAxis: boolean;
  yAxisOption: EChartsOption["yAxis"];
  yAxisIndexByMeasure: number[];
  hiddenMeasureKeys: Set<string>;
}): EChartsOption {
  const {
    chart,
    data,
    selectedChartType,
    palette,
    textColors,
    xKey,
    stackKey,
    isArea,
    hasDualAxis,
    yAxisOption,
    yAxisIndexByMeasure,
    hiddenMeasureKeys,
  } = options;

  const xValues: string[] = [];
  const seenX = new Set<string>();
  const stackValues: string[] = [];
  const seenStack = new Set<string>();
  for (const row of data) {
    const xLabel = String(row[xKey] ?? "");
    if (!seenX.has(xLabel)) {
      seenX.add(xLabel);
      xValues.push(xLabel);
    }
    const stackLabel = String(row[stackKey] ?? "");
    if (!seenStack.has(stackLabel)) {
      seenStack.add(stackLabel);
      stackValues.push(stackLabel);
    }
  }

  const colorForStack = new Map<string, string>();
  stackValues.forEach((value, index) => {
    colorForStack.set(value, palette[index % palette.length]);
  });

  const valueByXAndStack = new Map<string, Record<string, unknown>>();
  data.forEach((row) => {
    valueByXAndStack.set(`${String(row[xKey] ?? "")}::${String(row[stackKey] ?? "")}`, row);
  });

  const measureCount = chart.measures.length;
  // When multiple measures share a stack value, vary opacity per measure so
  // the measure groups stay visually distinguishable while every occurrence
  // of a given stack value keeps the same color.
  const opacityForMeasureIndex = (index: number): number =>
    measureCount <= 1 ? 1 : Math.max(0.45, 1 - (index * 0.4) / (measureCount - 1));
  // With a single measure, "China" is unambiguous and dedupes to one legend
  // entry. With multiple measures, "China" alone would be ambiguous on both
  // the legend and the axis tooltip (which value belongs to which measure?),
  // so disambiguate as "China (Net Revenue)" / "China (WoW Difference)".
  const seriesName = (stackValue: string, measure: (typeof chart.measures)[number]): string =>
    measureCount > 1 ? `${stackValue} (${measure.label ?? measure.key})` : stackValue;

  const series: NonNullable<EChartsOption["series"]> = [];
  const legendNamesByMeasure: string[][] = chart.measures.map(() => []);
  chart.measures.forEach((measure, measureIndex) => {
    const resolvedType = resolveSeriesType(selectedChartType, measure.encoding);
    stackValues.forEach((stackValue) => {
      const color = colorForStack.get(stackValue);
      const name = seriesName(stackValue, measure);
      legendNamesByMeasure[measureIndex].push(name);
      series.push({
        name,
        type: resolvedType,
        stack: resolvedType === "bar" ? measure.key : undefined,
        yAxisIndex: yAxisIndexByMeasure[measureIndex],
        smooth: chart.is_trend && resolvedType === "line",
        connectNulls: false,
        itemStyle: { color, opacity: opacityForMeasureIndex(measureIndex) },
        lineStyle:
          resolvedType === "line"
            ? { color, opacity: opacityForMeasureIndex(measureIndex), type: measure.predicted ? "dashed" : "solid" }
            : undefined,
        areaStyle: isArea ? { opacity: 0.25 * opacityForMeasureIndex(measureIndex), color } : undefined,
        data: xValues.map((xValue) => {
          const row = valueByXAndStack.get(`${xValue}::${stackValue}`);
          const raw = row ? row[measure.key] : null;
          return raw == null ? null : toNumeric(raw);
        }),
      });
    });
  });

  const showAxisChrome = xValues.length > 1;

  // Reserve one extra legend row's worth of height per additional measure;
  // the base 78px/22px already covers the single-row (measureCount <= 1) case.
  const extraRowsHeight = (measureCount - 1) * STACKED_LEGEND_ROW_HEIGHT_PX;
  const legendSelected: Record<string, boolean> = {};
  chart.measures.forEach((measure) => {
    if (!hiddenMeasureKeys.has(measure.key)) {
      return;
    }
    stackValues.forEach((stackValue) => {
      legendSelected[seriesName(stackValue, measure)] = false;
    });
  });
  const legend = buildPerMeasureLegendRows(measureCount, textColors, legendNamesByMeasure, legendSelected);

  return {
    color: palette,
    tooltip: { trigger: "axis" },
    title: buildLegendCaption(
      chart.stack_dimension?.label,
      textColors,
      extraRowsHeight + LEGEND_CAPTION_BOTTOM_PX,
    ),
    legend,
    grid: { left: 48, right: hasDualAxis ? 60 : 24, top: 32, bottom: 78 + extraRowsHeight, containLabel: true },
    xAxis: {
      type: "category",
      data: xValues,
      ...buildDimensionAxisName(
        chart.dimension.type === "category" && !showAxisChrome ? undefined : chart.dimension.label ?? xKey,
        textColors,
      ),
      axisLabel: { show: showAxisChrome, color: textColors.secondary },
      axisLine: { show: showAxisChrome },
      axisTick: { show: showAxisChrome },
    },
    yAxis: yAxisOption,
    series,
  } as EChartsOption;
}
